import { Alert, Button, Icon, Link, Stack, Text, Tooltip, tokens } from '@hotjar/hotsauce';
import { Trans, useTranslation } from '@hotjar/translations';
import styled from '@emotion/styled';
import { Children } from 'react';
import type { SurveyQuestion } from '../../../../domain/Surveys/Questions';
import type { Survey } from '../../../../domain/Surveys/Survey/Survey';
import { getQuestionLabelAndIcon } from '../../utils';
import { useTargetingDevices } from './useTargetingDevices';
import { useTargetingPages } from './useTargetingPages';
import { useTargetingUsers } from './useTargetingUsers';
import type { TargetEntry } from '../../../common/Wizard/common/targeting';

const DescriptionText = styled(Text)`
  max-width: calc(${tokens.spacer.x1} * 44);
`;

const BoldText = styled(Text)`
  font-weight: bold;
`;

const EditButton = styled(Button)`
  align-self: start;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${tokens.spacer.x1};
`;

const ErrorLink = styled(Link)`
  ${tokens.text('body2')};
  color: ${tokens.color.text_danger};
`;

const ErrorSubtitleLink = styled(Link)`
  ${tokens.text('subtitle1')};
  color: ${tokens.color.text_danger};
  align-self: start;
`;

type Text = string | number;

const TranslatedText = ({ children, i18nKey }: { children: Text | Text[]; i18nKey: string }) => {
  const value = Array.isArray(children) ? children.join(', ') : children;
  const count = Array.isArray(children) ? children.length : 1;

  if (count === 0) return null;

  return (
    <Text as="span" textType="body2">
      <Trans i18nKey={`feedback:survey-wizard-summary.${i18nKey}`} count={count}>
        label <BoldText>{{ value }}</BoldText>
      </Trans>
    </Text>
  );
};

const InvalidText = ({ children, onClick }: { children: Text | Text[]; onClick?: () => void }) => {
  const errors = Children.toArray(children);

  if (errors.length === 0) return null;

  if (errors.length === 1)
    return (
      <ErrorLink kind="inline" as="button" onClick={onClick}>
        {errors[0].toString()}
      </ErrorLink>
    );

  return (
    <ul>
      {Children.map(errors, (error) => (
        <li key={error.toString()}>
          <ErrorLink kind="inline" as="button" onClick={onClick}>
            {error.toString()}
          </ErrorLink>
        </li>
      ))}
    </ul>
  );
};

type SummaryRowProps = {
  icon: React.ComponentProps<typeof Icon>['icon'];
  iconTooltip: string;
  isValid: boolean;
  children: React.ReactNode;
  onPreview?: () => void;
};

const SummaryRow = ({ icon, iconTooltip, isValid, children, onPreview }: SummaryRowProps) => {
  return (
    <Row role={isValid ? '' : 'alert'} onMouseEnter={onPreview} onFocusCapture={onPreview}>
      <Tooltip text={iconTooltip}>
        <Icon
          icon={icon}
          description={iconTooltip}
          color={isValid ? 'text_default' : 'text_danger'}
        />
      </Tooltip>
      {children}
    </Row>
  );
};

type ValidationRowProps = {
  icon: React.ComponentProps<typeof Icon>['icon'];
  iconTooltip: string;
  isValid: boolean;
  value: Text | Text[];
  i18nKey: string;
  onErrorClicked: () => void;
};

const ValidationRow = ({
  icon,
  iconTooltip,
  isValid,
  i18nKey,
  value,
  onErrorClicked,
}: ValidationRowProps) => {
  return (
    <SummaryRow icon={icon} iconTooltip={iconTooltip} isValid={isValid}>
      {isValid ? (
        <TranslatedText i18nKey={i18nKey}>{value}</TranslatedText>
      ) : (
        <InvalidText onClick={onErrorClicked}>{value}</InvalidText>
      )}
    </SummaryRow>
  );
};

export type SummaryError = {
  text: string;
  onClick: () => void;
};

export type SurveyWizardLocation =
  | 'name'
  | 'targeting-section'
  | 'device-targeting'
  | 'page-targeting'
  | 'user-targeting'
  | 'traffic-targeting'
  | 'forwarding-email'
  | 'forwarding-slack'
  | 'forwarding-ms-teams'
  | 'forwarding-webhooks'
  | 'question-section'
  | 'question';

export type SurveyWizardNavigation =
  | { location: Omit<SurveyWizardLocation, 'question'> }
  | {
      location: 'question';
      questionUuid: string;
    };

export type SummaryProps = {
  name: string | undefined;
  description: string | undefined | null;
  questions: SurveyQuestion[];
  location: Survey['display_type'];
  targeting: TargetEntry[] | undefined;
  targetingPercentage: number;
  forwardingEmailEnabled: boolean;
  forwardingEmailAddresses?: string[] | null;
  forwardingSlackEnabled: boolean;
  forwardingSlackChannels?: string[] | null;
  forwardingMsTeamsEnabled: boolean;
  forwardingMsTeamsWebhookUrls?: string[] | null;
  forwardingWebhooksEnabled: boolean;
  forwardingWebhooksUrls?: string[] | null;
  errors: SummaryError[];
  mode: 'edit' | 'create';
  onNavigateClicked: (navigationTarget: SurveyWizardNavigation) => void;
  reloadPreview: (step: string) => void;
};

export const Summary = ({
  name,
  description,
  questions,
  location,
  targeting,
  targetingPercentage,
  forwardingEmailEnabled: emailEnabled,
  forwardingEmailAddresses: emailAddresses,
  forwardingSlackEnabled: slackEnabled,
  forwardingSlackChannels: slackChannels,
  forwardingMsTeamsEnabled: msTeamsEnabled,
  forwardingMsTeamsWebhookUrls: msTeamsWebhookUrls,
  forwardingWebhooksEnabled: webhooksEnabled,
  forwardingWebhooksUrls: webhooksUrls,
  errors,
  mode,
  onNavigateClicked,
  reloadPreview,
}: SummaryProps): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-wizard-summary' });
  const { t: tQuestionTypes } = useTranslation('feedback', {
    keyPrefix: 'survey.question-types',
  });
  const devices = useTargetingDevices(targeting);
  const pages = useTargetingPages(targeting);
  const users = useTargetingUsers(targeting);

  const hasMsTeams = msTeamsEnabled && (msTeamsWebhookUrls?.length ?? 0) > 0;
  const hasEmails = emailEnabled && (emailAddresses?.length ?? 0) > 0;
  const hasSlack = slackEnabled && (slackChannels?.length ?? 0) > 0;
  const hasWebhooks = webhooksEnabled && (webhooksUrls?.length ?? 0) > 0;

  return (
    <Stack spacing="x4" direction="column">
      <Stack direction="column">
        {name === '' ? (
          <span role="alert">
            <ErrorSubtitleLink
              as="button"
              kind="inline"
              onClick={() => onNavigateClicked({ location: 'name' })}
            >
              {t('name-not-set')}
            </ErrorSubtitleLink>
          </span>
        ) : (
          <Text textType="subtitle1">{name}</Text>
        )}

        {description !== undefined && description !== null && description !== '' && (
          <DescriptionText textType="body2" textColor="text_light">
            {description}
          </DescriptionText>
        )}
      </Stack>

      {errors.length > 0 && (
        <Stack direction="column" spacing="x1">
          {errors.map((error) => (
            <SummaryRow key={error.text} icon="error" iconTooltip={t('error')} isValid={false}>
              <InvalidText onClick={error.onClick}>{error.text}</InvalidText>
            </SummaryRow>
          ))}
        </Stack>
      )}

      <Stack direction="column">
        <Text textType="subtitle2">{t('questions', { count: questions.length })}</Text>
        {questions.map((question: SurveyQuestion) => {
          const { icon, label } = getQuestionLabelAndIcon(question.type);
          const isValid = question.text.length > 0;
          return (
            <SummaryRow
              key={question.uuid}
              icon={icon}
              iconTooltip={tQuestionTypes(label)}
              isValid={isValid}
              onPreview={() => reloadPreview(`question:${question.uuid}`)}
            >
              {isValid ? (
                <Text textType="body2">{question.text}</Text>
              ) : (
                <InvalidText
                  onClick={() =>
                    onNavigateClicked({ location: 'question', questionUuid: question.uuid })
                  }
                >
                  {t('question-text-not-set')}
                </InvalidText>
              )}
            </SummaryRow>
          );
        })}
      </Stack>

      {location === 'external_link' && mode === 'create' && (
        <Alert kind="info">{t('link-info')}</Alert>
      )}

      {location !== 'external_link' &&
        devices !== undefined &&
        pages !== undefined &&
        users !== undefined && (
          <Stack direction="column">
            <Text textType="subtitle2">{t('targeting')}</Text>

            <ValidationRow
              icon="device_multiple"
              iconTooltip={t('device-targeting')}
              isValid={devices.isValid}
              i18nKey="shows-on-device"
              value={devices.summaryText}
              onErrorClicked={() => onNavigateClicked({ location: 'device-targeting' })}
            />
            <ValidationRow
              icon="page_empty"
              iconTooltip={t('page-targeting')}
              i18nKey="shows-on-page"
              value={pages.summaryText}
              isValid={pages.isValid}
              onErrorClicked={() => onNavigateClicked({ location: 'page-targeting' })}
            />
            <ValidationRow
              icon="user_multiple"
              iconTooltip={t('user-targeting')}
              i18nKey="shows-to-user"
              value={users}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'user-targeting' })}
            />
            <ValidationRow
              icon="site"
              iconTooltip={t('traffic-coverage')}
              i18nKey="shows-to-traffic-percentage"
              value={targetingPercentage}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'traffic-coverage' })}
            />
            <EditButton
              kind="secondary"
              onClick={() => onNavigateClicked({ location: 'targeting-section' })}
            >
              {t('edit-targeting-settings')}
            </EditButton>
          </Stack>
        )}

      {(hasEmails || hasSlack || hasMsTeams || hasWebhooks) && (
        <Stack direction="column">
          <Text textType="subtitle2">{t('responses')}</Text>
          {hasEmails && (
            <ValidationRow
              icon="email"
              iconTooltip={t('email-forwarding')}
              i18nKey="send-by-email-to"
              value={emailAddresses ?? []}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'forwarding-email' })}
            />
          )}
          {hasSlack && (
            <ValidationRow
              icon="slack"
              iconTooltip={t('slack-forwarding')}
              i18nKey="send-to-the-slack-channel"
              value={slackChannels ?? []}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'forwarding-slack' })}
            />
          )}
          {hasMsTeams && (
            <ValidationRow
              icon="microsoft_teams"
              iconTooltip={t('ms-teams-forwarding')}
              i18nKey="forward-ms-teams"
              value={msTeamsWebhookUrls ?? []}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'forwarding-ms-teams' })}
            />
          )}
          {hasWebhooks && (
            <ValidationRow
              icon="webhooks"
              iconTooltip={t('webhooks-forwarding')}
              i18nKey="send-to-webhooks"
              value={webhooksUrls ?? []}
              isValid
              onErrorClicked={() => onNavigateClicked({ location: 'forwarding-webhooks' })}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
